import { useState } from "react";
import { META_MODULE_ENABLED } from "../../lib/features";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SlidersHorizontal,
  Wallet,
  CheckCircle2,
  Clock,
  Coins,
  AlertCircle,
  Percent,
  Layers,
  Save,
  ShieldCheck,
  QrCode,
  Sparkles,
  Smartphone,
  Info,
  Webhook,
  Users,
} from "lucide-react";

import {
  listTenants,
  listWallets,
  listReceipts,
  rechargeCredits,
  getPricingMatrix,
  updatePricingMatrix,
  listSubscriptionPlans,
  assignTenantPlan,
  getTenantBillingSummary,
  type CurrentUser,
  type ManualReceipt,
  type PricingMatrixItem,
  type SubscriptionPlanItem,
  type TenantBillingSummary,
} from "../../lib/api";

type ManualBillingViewProps = {
  token: string;
  user: CurrentUser;
  tenantId?: string;
};

export function ManualBillingView({ token, user, tenantId }: ManualBillingViewProps) {
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";
  const activeTenantId = tenantId || user.tenant_id;

  const [activeTab, setActiveTab] = useState<"wallets" | "pricing" | "plans" | "receipts">("wallets");
  const [searchQuery, setSearchQuery] = useState("");
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [isAssignPlanModalOpen, setIsAssignPlanModalOpen] = useState(false);

  // Form states for credit recharge
  const [targetTenantId, setTargetTenantId] = useState<string>("");
  const [creditAmount, setCreditAmount] = useState<number>(1000);
  const [chargeAmount, setChargeAmount] = useState<number>(10);
  const [rechargeNotes, setRechargeNotes] = useState<string>("Manual credit top-up");
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  // Form states for plan assignment
  const [assignTenantId, setAssignTenantId] = useState<string>("");
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [customMaxQr, setCustomMaxQr] = useState<number>(1);
  const [customOverdraft, setCustomOverdraft] = useState<number>(0);
  const [customQrRate, setCustomQrRate] = useState<string>("");

  // Pricing matrix editable state
  const [pricingEdits, setPricingEdits] = useState<Record<string, { base_cost: number; client_rate: number; markup_percent: number }>>({});
  const [pricingFeedback, setPricingFeedback] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // 1. Live Tenants (for Super Admin selector)
  const tenantsQuery = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: () => listTenants(token),
    enabled: isSuperAdmin,
  });

  // 2. Live Wallets
  const walletsQuery = useQuery({
    queryKey: ["billing", "wallets", activeTenantId],
    queryFn: () => listWallets(token, activeTenantId),
    refetchInterval: 10_000,
  });

  // 3. Live Receipts
  const receiptsQuery = useQuery({
    queryKey: ["billing", "receipts", activeTenantId],
    queryFn: () => listReceipts(token, activeTenantId),
    refetchInterval: 10_000,
  });

  // 4. Live Pricing Matrix
  const pricingQuery = useQuery({
    queryKey: ["billing", "pricing-matrix"],
    queryFn: () => getPricingMatrix(token),
  });

  // 5. Subscription Plans
  const plansQuery = useQuery({
    queryKey: ["billing", "plans"],
    queryFn: () => listSubscriptionPlans(token),
  });

  // 6. Tenant Billing Summary (for tenant-facing summary widget)
  const tenantSummaryQuery = useQuery({
    queryKey: ["billing", "summary", activeTenantId],
    queryFn: () => (activeTenantId ? getTenantBillingSummary(token, activeTenantId) : Promise.reject("No tenant")),
    enabled: Boolean(activeTenantId),
    refetchInterval: 15_000,
  });

  // Recharge Mutation
  const rechargeMutation = useMutation({
    mutationFn: (payload: { tenant_id: string; amount: number; credits: number; notes?: string }) =>
      rechargeCredits(token, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
      setFeedback({ type: "success", msg: `Successfully recharged ${creditAmount.toLocaleString()} credits.` });
      setTimeout(() => {
        setIsReceiptModalOpen(false);
        setFeedback(null);
      }, 1200);
    },
    onError: (err: any) => {
      setFeedback({ type: "error", msg: err?.message || "Failed to recharge credits." });
    },
  });

  // Plan Assignment Mutation
  const assignPlanMutation = useMutation({
    mutationFn: (payload: { tenant_id: string; data: any }) =>
      assignTenantPlan(token, payload.tenant_id, payload.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing"] });
      setFeedback({ type: "success", msg: "Subscription plan & quotas successfully updated!" });
      setTimeout(() => {
        setIsAssignPlanModalOpen(false);
        setFeedback(null);
      }, 1200);
    },
    onError: (err: any) => {
      setFeedback({ type: "error", msg: err?.message || "Failed to update tenant plan." });
    },
  });

  // Pricing Matrix Update Mutation
  const updatePricingMutation = useMutation({
    mutationFn: (items: any[]) => updatePricingMatrix(token, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing", "pricing-matrix"] });
      setPricingFeedback("Platform pricing rates saved successfully!");
      setTimeout(() => setPricingFeedback(null), 2500);
    },
    onError: (err: any) => {
      setPricingFeedback(`Error saving rates: ${err?.message || "Unknown error"}`);
    },
  });

  const wallets = walletsQuery.data || [];
  const totalBalance = wallets.reduce((sum, w) => sum + Number(w.balance || 0), 0);
  const receipts: ManualReceipt[] = receiptsQuery.data || [];
  const pricingItems: PricingMatrixItem[] = pricingQuery.data || [];
  const subscriptionPlans: SubscriptionPlanItem[] = plansQuery.data || [];
  const tenantSummary: TenantBillingSummary | undefined = tenantSummaryQuery.data;

  const filteredReceipts = receipts.filter(
    (r) =>
      r.payment_reference.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.notes && r.notes.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  function handleRechargeSubmit(e: React.FormEvent) {
    e.preventDefault();
    const chosenTenant = targetTenantId || tenantsQuery.data?.[0]?.id || user.tenant_id;
    if (!chosenTenant) {
      setFeedback({ type: "error", msg: "Please select a target organization." });
      return;
    }
    rechargeMutation.mutate({
      tenant_id: chosenTenant,
      amount: chargeAmount,
      credits: creditAmount,
      notes: rechargeNotes,
    });
  }

  function handleAssignPlanSubmit(e: React.FormEvent) {
    e.preventDefault();
    const chosenTenant = assignTenantId || tenantsQuery.data?.[0]?.id;
    if (!chosenTenant) return;
    assignPlanMutation.mutate({
      tenant_id: chosenTenant,
      data: {
        plan_id: selectedPlanId || undefined,
        max_qr_instances: customMaxQr,
        overdraft_limit: customOverdraft,
        custom_qr_rate: customQrRate ? Number(customQrRate) : undefined,
      },
    });
  }

  function handleRateChange(id: string, field: "base_cost" | "client_rate" | "markup_percent", val: number) {
    const orig = pricingItems.find((p) => p.id === id);
    if (!orig) return;
    const current = pricingEdits[id] || {
      base_cost: Number(orig.base_cost),
      client_rate: Number(orig.client_rate),
      markup_percent: Number(orig.markup_percent),
    };

    const next = { ...current, [field]: val };
    if (field === "markup_percent" && next.base_cost > 0) {
      next.client_rate = Number((next.base_cost * (1 + val / 100)).toFixed(4));
    } else if (field === "client_rate" && next.base_cost > 0) {
      next.markup_percent = Number((((val - next.base_cost) / next.base_cost) * 100).toFixed(1));
    }
    setPricingEdits({ ...pricingEdits, [id]: next });
  }

  function handleSavePricing() {
    const itemsToSave = pricingItems.map((item) => {
      const edit = pricingEdits[item.id];
      return {
        id: item.id,
        base_cost: edit ? edit.base_cost : Number(item.base_cost),
        client_rate: edit ? edit.client_rate : Number(item.client_rate),
        markup_percent: edit ? edit.markup_percent : Number(item.markup_percent),
        is_active: item.is_active,
      };
    });
    updatePricingMutation.mutate(itemsToSave);
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 text-white p-6 rounded-2xl shadow-lg">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-white/15 text-white text-xs font-semibold backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Commercial Billing &amp; Pricing Center</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Financial Wallet &amp; Rates Management</h1>
          <p className="text-emerald-100 text-xs md:text-sm max-w-2xl">
            Configure channel pricing, monitor live wallet balances, enforce WhatsApp number quotas, and manage subscription plans.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <button
              onClick={() => {
                setTargetTenantId(tenantsQuery.data?.[0]?.id || "");
                setIsReceiptModalOpen(true);
              }}
              className="px-4 py-2.5 bg-white text-emerald-700 hover:bg-emerald-50 rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-2"
            >
              <Coins className="w-4 h-4" />
              <span>Direct Top-Up / Recharge</span>
            </button>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("wallets")}
          className={`px-4 py-3 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "wallets"
              ? "border-emerald-600 text-emerald-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Wallet className="w-4 h-4" />
          <span>Wallets &amp; Balance</span>
        </button>

        {isSuperAdmin && (
          <button
            onClick={() => setActiveTab("pricing")}
            className={`px-4 py-3 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === "pricing"
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>Pricing Matrix &amp; Margins</span>
          </button>
        )}

        {isSuperAdmin && META_MODULE_ENABLED && (
          <button
            onClick={() => setActiveTab("plans")}
            className={`px-4 py-3 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
              activeTab === "plans"
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Layers className="w-4 h-4" />
            <span>Subscription Plans &amp; Quotas</span>
          </button>
        )}

        <button
          onClick={() => setActiveTab("receipts")}
          className={`px-4 py-3 text-xs font-semibold border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "receipts"
              ? "border-emerald-600 text-emerald-700"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Clock className="w-4 h-4" />
          <span>Transactions &amp; Receipts</span>
        </button>
      </div>

      {/* TAB 1: WALLETS & BALANCE */}
      {activeTab === "wallets" && (
        <div className="space-y-6">
          {/* Tenant-facing summary widget if tenant user */}
          {tenantSummary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between text-slate-500 mb-2">
                  <span className="text-xs font-medium uppercase tracking-wider">Available Balance</span>
                  <Wallet className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-2xl font-bold font-mono text-slate-900">
                  {tenantSummary.wallet_balance.toFixed(4)} <span className="text-xs font-normal text-slate-500">{tenantSummary.currency}</span>
                </div>
                {tenantSummary.overdraft_limit > 0 && (
                  <p className="text-[11px] text-emerald-600 mt-1">
                    Includes +{tenantSummary.overdraft_limit.toFixed(2)} overdraft grace
                  </p>
                )}
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between text-slate-500 mb-2">
                  <span className="text-xs font-medium uppercase tracking-wider">Active Plan</span>
                  <Layers className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-lg font-bold text-slate-900 truncate">
                  {tenantSummary.plan?.name || "Pay As You Go"}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {tenantSummary.plan ? `${tenantSummary.plan.included_qr_messages.toLocaleString()} free msgs/mo` : "Standard rates"}
                </p>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between text-slate-500 mb-2">
                  <span className="text-xs font-medium uppercase tracking-wider">QR Instance Quota</span>
                  <Smartphone className="w-4 h-4 text-purple-600" />
                </div>
                <div className="text-2xl font-bold font-mono text-slate-900">
                  {tenantSummary.qr_instances.used} / {tenantSummary.qr_instances.max}
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      tenantSummary.qr_instances.used >= tenantSummary.qr_instances.max ? "bg-amber-500" : "bg-emerald-500"
                    }`}
                    style={{
                      width: `${Math.min(100, (tenantSummary.qr_instances.used / (tenantSummary.qr_instances.max || 1)) * 100)}%`,
                    }}
                  />
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex items-center justify-between text-slate-500 mb-2">
                  <span className="text-xs font-medium uppercase tracking-wider">Your QR Rate</span>
                  <QrCode className="w-4 h-4 text-teal-600" />
                </div>
                <div className="text-2xl font-bold font-mono text-emerald-600">
                  ${tenantSummary.rates.qr_standard.toFixed(4)}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">Per outbound broadcast message</p>
              </div>
            </div>
          )}

          {/* Wallets Grid / List */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-600" />
                <span>Organization Credit Accounts</span>
              </h3>
              <span className="text-xs text-slate-500">
                Total Reserve: <strong className="font-mono text-emerald-600">{totalBalance.toFixed(4)} CREDITS</strong>
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50/75 border-b border-slate-100 text-slate-500 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="py-3 px-4">Organization / Tenant</th>
                    <th className="py-3 px-4">Account Type</th>
                    <th className="py-3 px-4">Current Balance</th>
                    <th className="py-3 px-4">Status</th>
                    {isSuperAdmin && <th className="py-3 px-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {wallets.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-slate-400">
                        No active wallet accounts found.
                      </td>
                    </tr>
                  ) : (
                    wallets.map((w) => {
                      const tInfo = tenantsQuery.data?.find((t) => t.id === w.tenant_id);
                      return (
                        <tr key={w.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="font-semibold text-slate-800">{tInfo ? tInfo.name : "Platform Reserve"}</div>
                            <div className="font-mono text-[11px] text-slate-400">{w.tenant_id || w.id}</div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700">
                              {w.account_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 font-mono font-bold text-slate-900">
                            {Number(w.balance).toFixed(4)} <span className="text-[10px] text-slate-400">{w.currency}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                Number(w.balance) > 10
                                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  : Number(w.balance) > 0
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-rose-50 text-rose-700 border border-rose-200"
                              }`}
                            >
                              {Number(w.balance) > 0 ? "Funded" : "Zero / Exhausted"}
                            </span>
                          </td>
                          {isSuperAdmin && (
                            <td className="py-3 px-4 text-right">
                              <button
                                onClick={() => {
                                  setTargetTenantId(w.tenant_id || "");
                                  setIsReceiptModalOpen(true);
                                }}
                                className="px-2.5 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded text-xs font-semibold transition-colors"
                              >
                                Top Up
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: PRICING MATRIX & MARGINS (SUPER ADMIN) */}
      {activeTab === "pricing" && isSuperAdmin && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-slate-900">Platform Rate Matrix &amp; Profit Margins</h2>
                <p className="text-xs text-slate-500">
                  Configure outbound platform usage rates. Existing currency and balances remain unchanged.
                </p>
              </div>
              <button
                onClick={handleSavePricing}
                disabled={updatePricingMutation.isPending}
                className="btn-primary text-xs flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                <span>{updatePricingMutation.isPending ? "Saving..." : "Save Rate Changes"}</span>
              </button>
            </div>

            {pricingFeedback && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{pricingFeedback}</span>
              </div>
            )}

            <div className="overflow-x-auto border border-slate-100 rounded-lg">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase font-semibold text-[11px]">
                  <tr>
                    <th className="py-3 px-4">Channel</th>
                    <th className="py-3 px-4">Category / Purpose</th>
                    <th className="py-3 px-4">Base Cost ($)</th>
                    <th className="py-3 px-4">Profit Margin (%)</th>
                    <th className="py-3 px-4">Client Price ($)</th>
                    <th className="py-3 px-4 text-center">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pricingItems.filter((item) => META_MODULE_ENABLED || item.channel_type === "qr_whatsapp").map((item) => {
                    const edit = pricingEdits[item.id] || {
                      base_cost: Number(item.base_cost),
                      client_rate: Number(item.client_rate),
                      markup_percent: Number(item.markup_percent),
                    };
                    const isQR = item.channel_type === "qr_whatsapp";

                    return (
                      <tr key={item.id} className="hover:bg-slate-50/50">
                        <td className="py-3.5 px-4 font-semibold text-slate-800">
                          {isQR ? (
                            <span className="inline-flex items-center gap-1.5 text-teal-700 bg-teal-50 px-2 py-0.5 rounded font-mono">
                              <QrCode className="w-3.5 h-3.5" /> QR Module
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-blue-700 bg-blue-50 px-2 py-0.5 rounded font-mono">
                              <ShieldCheck className="w-3.5 h-3.5" /> Meta WABA
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 px-4 font-medium text-slate-700 capitalize">
                          {item.category}
                        </td>
                        <td className="py-3.5 px-4">
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={edit.base_cost}
                            onChange={(e) => handleRateChange(item.id, "base_cost", Number(e.target.value))}
                            className="modern-input w-24 text-xs font-mono"
                          />
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.1"
                              value={edit.markup_percent}
                              onChange={(e) => handleRateChange(item.id, "markup_percent", Number(e.target.value))}
                              className="modern-input w-20 text-xs font-mono font-bold text-emerald-600"
                            />
                            <Percent className="w-3 h-3 text-slate-400" />
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={edit.client_rate}
                            onChange={(e) => handleRateChange(item.id, "client_rate", Number(e.target.value))}
                            className="modern-input w-28 text-xs font-mono font-bold text-slate-900 bg-emerald-50/50"
                          />
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3 text-xs text-slate-600">
              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <strong className="text-slate-800">Platform usage charges</strong>: QR pricing covers hosting, storage, support and maintenance. It is not a Meta message fee or a guarantee of delivery. INR pay-as-you-go pricing is proposed separately and is not activated here.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: SUBSCRIPTION PLANS & QUOTAS (SUPER ADMIN) */}
      {activeTab === "plans" && isSuperAdmin && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-slate-900">Subscription Plans &amp; Resource Tiers</h2>
              <p className="text-xs text-slate-500">
                Configure monthly SaaS tiers, included free message allowances, and maximum WhatsApp linked instances.
              </p>
            </div>
            <button
              onClick={() => {
                setAssignTenantId(tenantsQuery.data?.[0]?.id || "");
                setSelectedPlanId(subscriptionPlans[0]?.id || "");
                setIsAssignPlanModalOpen(true);
              }}
              className="btn-primary text-xs flex items-center gap-1.5"
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Assign Plan to Organization</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {subscriptionPlans.map((plan) => (
              <div
                key={plan.id}
                className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-all"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full">
                      {plan.slug}
                    </span>
                    <span className="text-xs font-medium text-slate-400">Monthly</span>
                  </div>

                  <div>
                    <h3 className="text-xl font-extrabold text-slate-900">{plan.name}</h3>
                    <div className="text-3xl font-extrabold text-slate-900 font-mono mt-1">
                      ${Number(plan.monthly_price).toFixed(0)} <span className="text-xs font-normal text-slate-500">/mo</span>
                    </div>
                  </div>

                  <ul className="space-y-2.5 text-xs text-slate-600 pt-3 border-t border-slate-100">
                    <li className="flex items-center gap-2 text-emerald-700 bg-emerald-50/75 p-1.5 rounded font-semibold text-[11px]">
                      <Users className="w-3.5 h-3.5 shrink-0 text-emerald-600" />
                      <span><strong>Unlimited Team Agents</strong> (Zero Seat Fees)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span><strong>1</strong> QR WhatsApp Companion Number</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-blue-600 shrink-0" />
                      <span><strong>1</strong> Meta Verified Cloud API Number</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Webhook className="w-4 h-4 text-purple-600 shrink-0" />
                      <span><strong>Webhooks</strong> &amp; <strong>REST API</strong> Access Included</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <QrCode className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span><strong>{plan.included_qr_messages.toLocaleString()}</strong> Free QR Messages/month</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Coins className="w-4 h-4 text-slate-500 shrink-0" />
                      <span>Over-quota QR: <strong>${Number(plan.over_quota_qr_rate).toFixed(4)}</strong>/msg</span>
                    </li>
                    <li className="flex items-center gap-2 text-[11px] text-slate-500">
                      <span>Add-on: Extra QR Number slot <strong>+$10/mo</strong></span>
                    </li>
                    <li className="flex items-start gap-2 pt-2 border-t border-slate-100 text-[11px] text-amber-700 bg-amber-50/75 p-2 rounded-lg">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600" />
                      <span>
                        <strong>Meta charges not included</strong>: Cloud API conversation fees (Auth/Utility/Marketing) are billed separately on Pay-As-You-Go from your credit wallet.
                      </span>
                    </li>
                  </ul>


                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: RECEIPTS & TRANSACTION AUDIT */}
      {activeTab === "receipts" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-4 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-72">
              <input
                type="text"
                placeholder="Search transaction reference..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="modern-input w-full text-xs pl-8"
              />
              <Clock className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
            </div>
            <span className="text-xs text-slate-500">{filteredReceipts.length} records recorded</span>
          </div>

          <div className="overflow-x-auto border border-slate-100 rounded-lg">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600 uppercase font-semibold text-[11px]">
                <tr>
                  <th className="py-3 px-4">Payment Reference</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Credits Granted</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Notes</th>
                  <th className="py-3 px-4">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredReceipts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-slate-400">
                      No transaction records found.
                    </td>
                  </tr>
                ) : (
                  filteredReceipts.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50/50">
                      <td className="py-3 px-4 font-mono font-semibold text-slate-800">{r.payment_reference}</td>
                      <td className="py-3 px-4 font-mono font-medium text-slate-700">${Number(r.amount).toFixed(2)}</td>
                      <td className="py-3 px-4 font-mono font-bold text-emerald-600">+{Number(r.credits_to_grant).toLocaleString()}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3 h-3" />
                          {r.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-500 max-w-xs truncate">{r.notes || "—"}</td>
                      <td className="py-3 px-4 font-mono text-slate-400 text-[11px]">
                        {new Date(r.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Credit Recharge Modal */}
      {isReceiptModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsReceiptModalOpen(false)}>
          <div className="modal-card max-w-md w-full p-6 relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900 mb-1">Direct Credit Top-Up</h3>
            <p className="text-xs text-slate-500 mb-4">
              Allocate WhatsApp message credits directly to an organization wallet.
            </p>

            {feedback && (
              <div
                className={`p-3 mb-4 rounded-lg text-xs flex items-center gap-2 ${
                  feedback.type === "success"
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                    : "bg-rose-50 border border-rose-200 text-rose-700"
                }`}
              >
                {feedback.type === "success" ? (
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                )}
                <span>{feedback.msg}</span>
              </div>
            )}

            <form onSubmit={handleRechargeSubmit} className="space-y-3.5">
              {isSuperAdmin && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Target Organization *
                  </label>
                  <select
                    value={targetTenantId}
                    onChange={(e) => setTargetTenantId(e.target.value)}
                    className="modern-input w-full text-xs"
                    required
                  >
                    {tenantsQuery.data?.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Credits to Grant *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(Number(e.target.value))}
                    className="modern-input w-full text-xs font-mono font-bold text-emerald-600"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Fee Charged ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={chargeAmount}
                    onChange={(e) => setChargeAmount(Number(e.target.value))}
                    className="modern-input w-full text-xs font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Notes / Reason</label>
                <input
                  type="text"
                  value={rechargeNotes}
                  onChange={(e) => setRechargeNotes(e.target.value)}
                  placeholder="e.g. Monthly allocation or invoice payment"
                  className="modern-input w-full text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setIsReceiptModalOpen(false)}
                  disabled={rechargeMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary text-xs flex items-center gap-1.5"
                  disabled={rechargeMutation.isPending}
                >
                  <Coins className="w-3.5 h-3.5" />
                  <span>{rechargeMutation.isPending ? "Allocating..." : "Confirm Top Up"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Plan Assignment Modal */}
      {isAssignPlanModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAssignPlanModalOpen(false)}>
          <div className="modal-card max-w-md w-full p-6 relative shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900 mb-1">Assign Plan &amp; Quotas</h3>
            <p className="text-xs text-slate-500 mb-4">
              Bind a subscription plan or customize individual quotas for this organization.
            </p>

            <form onSubmit={handleAssignPlanSubmit} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Organization *</label>
                <select
                  value={assignTenantId}
                  onChange={(e) => setAssignTenantId(e.target.value)}
                  className="modern-input w-full text-xs"
                  required
                >
                  {tenantsQuery.data?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Subscription Plan</label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => {
                    setSelectedPlanId(e.target.value);
                    const p = subscriptionPlans.find((x) => x.id === e.target.value);
                    if (p) {
                      setCustomMaxQr(p.max_qr_instances);
                    }
                  }}
                  className="modern-input w-full text-xs"
                >
                  {subscriptionPlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} (${Number(p.monthly_price).toFixed(0)}/mo - {p.max_qr_instances} QR numbers)
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Max QR Numbers</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={customMaxQr}
                    onChange={(e) => setCustomMaxQr(Number(e.target.value))}
                    className="modern-input w-full text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Overdraft Limit ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="5"
                    value={customOverdraft}
                    onChange={(e) => setCustomOverdraft(Number(e.target.value))}
                    className="modern-input w-full text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Custom QR Rate ($/msg override)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  placeholder="Leave empty to use plan default"
                  value={customQrRate}
                  onChange={(e) => setCustomQrRate(e.target.value)}
                  className="modern-input w-full text-xs"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  onClick={() => setIsAssignPlanModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary text-xs flex items-center gap-1.5"
                  disabled={assignPlanMutation.isPending}
                >
                  <span>{assignPlanMutation.isPending ? "Saving..." : "Apply Plan"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
